import { useEffect, useMemo, useState } from 'react'
import { Alert, Box, Stack, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material'
import FormContentInputAdapter from './FormContentInputAdapter'
import PayloadEditor from '../asset/PayloadEditor'
import { safeDecodeFormContentYaml } from './formContentYaml'

type Mode = 'visual' | 'yaml'

type FormPayloadEditorProps = {
  yaml: string
  editable: boolean
  onChange?: (yaml: string) => void
  jsonSchema?: Record<string, unknown>
}

/**
 * The Form's payload editor: a single YAML string presented either as the visual page editor or as the raw YAML, with
 * a toggle between them. Both views read and write the same string, so either edit flows through to save and preview.
 * Visual mode cannot render YAML that doesn't decode into pages, so that toggle is disabled (and undecodable content
 * opens in YAML mode) rather than silently dropping the user's text.
 */
const FormPayloadEditor = ({ yaml, editable, onChange, jsonSchema }: FormPayloadEditorProps) => {
  const decodeError = useMemo(() => safeDecodeFormContentYaml(yaml).error, [yaml])
  const [mode, setMode] = useState<Mode>(() => (safeDecodeFormContentYaml(yaml).error ? 'yaml' : 'visual'))

  // External changes (or YAML edits) can make the content undecodable while visual mode is active; drop to YAML so the
  // user sees and can fix the raw text instead of an empty editor.
  useEffect(() => {
    if (decodeError && mode === 'visual') setMode('yaml')
  }, [decodeError, mode])

  return (
    <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={mode}
        onChange={(_, next: Mode | null) => next && setMode(next)}
        sx={{ alignSelf: 'flex-start', flexShrink: 0 }}
      >
        <Tooltip title={decodeError ? 'Fix YAML errors to use the visual editor' : ''}>
          {/* span keeps the tooltip working while the button is disabled */}
          <span>
            <ToggleButton value="visual" disabled={!!decodeError}>
              Visual
            </ToggleButton>
          </span>
        </Tooltip>
        <ToggleButton value="yaml">YAML</ToggleButton>
      </ToggleButtonGroup>

      {mode === 'visual' ? (
        // Long forms scroll inside this panel (as the YAML editor already does) so the page itself never grows.
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <FormContentInputAdapter yaml={yaml} editable={editable} onYamlChange={onChange} />
        </Box>
      ) : (
        <>
          <PayloadEditor value={yaml} onChange={onChange} editable={editable} jsonSchema={jsonSchema} />
          {decodeError && <Alert severity="error">{decodeError}</Alert>}
        </>
      )}
    </Stack>
  )
}

export default FormPayloadEditor
